import React from 'react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useFragment } from 'react-relay';
import { FireScreenAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { IssuesWarningCountProvider } from '@atlassian/jira-software-releases-version-issue-warning-controller/src/ui/index.tsx';
import type { screenAnalytics_softwareReleasesVersionDetailIssueList_IssueListScreenAnalytics_jiraIssueEdgesReference$key } from './__generated__/screenAnalytics_softwareReleasesVersionDetailIssueList_IssueListScreenAnalytics_jiraIssueEdgesReference.graphql';
import type { screenAnalytics_softwareReleasesVersionDetailIssueList_IssueListScreenAnalytics_jiraVersionReference$key } from './__generated__/screenAnalytics_softwareReleasesVersionDetailIssueList_IssueListScreenAnalytics_jiraVersionReference.graphql';

type Props = {
	jiraIssueEdgesReference: screenAnalytics_softwareReleasesVersionDetailIssueList_IssueListScreenAnalytics_jiraIssueEdgesReference$key;
	jiraVersionReference: screenAnalytics_softwareReleasesVersionDetailIssueList_IssueListScreenAnalytics_jiraVersionReference$key;
};

export const IssueListScreenAnalytics = ({
	jiraIssueEdgesReference,
	jiraVersionReference,
}: Props): React.JSX.Element => {
	const jiraIssueEdgeReferenceData = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment screenAnalytics_softwareReleasesVersionDetailIssueList_IssueListScreenAnalytics_jiraIssueEdgesReference on JiraIssueEdge
			@relay(plural: true) {
				node {
					summarisedDeployments @include(if: $shouldIncludeDevOpsFieldsViaAgs) {
						count
					}
					summarisedBuilds @include(if: $shouldIncludeDevOpsFieldsViaAgs) {
						count
					}
					summarisedFeatureFlags {
						totalCount
					}
					devSummaryCache @skip(if: $isDevopsFeatureDisabledInFedRamp) {
						devSummary {
							pullrequest {
								overall {
									count
								}
							}
							branch {
								overall {
									count
								}
							}
							review {
								overall {
									count
								}
							}
							commit {
								overall {
									count
								}
							}
							build {
								overall {
									count
								}
							}
							deploymentEnvironments {
								overall {
									topEnvironments {
										status
									}
								}
							}
						}
					}
				}
				...ui_softwareReleasesVersionIssueWarningController_IssuesWarningCountProvider_issueEdges
			}
		`,
		jiraIssueEdgesReference,
	);

	const warningConfigFragment = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment screenAnalytics_softwareReleasesVersionDetailIssueList_IssueListScreenAnalytics_jiraVersionReference on JiraVersion {
				...ui_softwareReleasesVersionIssueWarningController_IssuesWarningCountProvider_jiraVersion
			}
		`,
		jiraVersionReference,
	);

	const nodes = jiraIssueEdgeReferenceData.map((edge) => edge.node).filter(Boolean);

	// TODO: This will be improved by getting these values from the server either from Data Depot
	// or ARI Graph Store relationships. The current code has a bug that might result incorrect
	// boolean due to pagination. Also this is ugly to calculate in FE.
	const hasBambooDeployment = nodes.some(
		(node) =>
			(
				node.devSummaryCache?.devSummary?.deploymentEnvironments?.overall?.topEnvironments ?? []
			).filter((deployment) => deployment.status === 'DEPLOYED').length > 0,
	);
	const hasDeployments = nodes.some(
		(node) => node.summarisedDeployments?.count != null && node.summarisedDeployments?.count > 0,
	);
	const hasFeatureFlags = nodes.some((node) => (node.summarisedFeatureFlags?.totalCount ?? 0) > 0);
	const hasCommits = nodes.some(
		(node) => Number(node.devSummaryCache?.devSummary?.commit?.overall?.count) > 0,
	);
	const hasPullRequests = nodes.some(
		(node) => Number(node.devSummaryCache?.devSummary?.pullrequest?.overall?.count) > 0,
	);
	const hasBranches = nodes.some(
		(node) => Number(node.devSummaryCache?.devSummary?.branch?.overall?.count) > 0,
	);
	const hasBuilds = nodes.some(
		(node) =>
			Number(node.devSummaryCache?.devSummary?.build?.overall?.count) > 0 ||
			Number(node.summarisedBuilds?.count) > 0,
	);
	const hasReviews = nodes.some(
		(node) => Number(node.devSummaryCache?.devSummary?.review?.overall?.count) > 0,
	);

	return (
		<IssuesWarningCountProvider
			jiraVersion={warningConfigFragment}
			issueEdges={jiraIssueEdgeReferenceData}
		>
			{(warningCount) => (
				<FireScreenAnalytics
					attributes={{
						hasBamboo: hasBambooDeployment,
						hasBranches,
						hasBuilds,
						hasCommits,
						hasDeployments,
						hasFeatureFlags,
						hasPullRequests,
						hasReviews,
						warningCount,
					}}
				/>
			)}
		</IssuesWarningCountProvider>
	);
};
